import React, { useCallback, useEffect, useState } from "react";
import {
  FaBuilding,
  FaEnvelope,
  FaGlobe,
  FaPhone,
  FaShieldAlt,
  FaUser,
  FaUsers,
} from "react-icons/fa";
import { apihubGet, apihubPost, apihubUpload } from "../../api/apihub";
import { useProjectPhone } from "../../hooks/useProjectPhone";

const PROFILE_FIELDS = [
  { key: "user_name", label: "User Name", icon: FaUser, altKey: "display_name" },
  { key: "company", label: "Company Name", icon: FaBuilding },
  { key: "email", label: "Email", icon: FaEnvelope },
  { key: "contact", label: "Contact", icon: FaPhone },
  { key: "companySize", label: "Company Size", icon: FaUsers },
  { key: "companywebsite", label: "Company Website", icon: FaGlobe },
];

const VERTICALS = [
  "Automotive",
  "Beauty, Spa and Salon",
  "Clothing and Apparel",
  "Education",
  "Entertainment",
  "Event Planning and Service",
  "Finance and Banking",
  "Food and Grocery",
  "Public Service",
  "Hotel and Lodging",
  "Medical and Health",
  "Non-profit",
  "Professional Services",
  "Shopping and Retail",
  "Travel and Transportation",
  "Restaurant",
  "Other",
];

const displayValue = (value) => {
  if (value === null || value === undefined || value === "") return "Not provided";
  return String(value);
};

const Toggle = ({ checked, onChange, disabled, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    className={`greeting-toggle${checked ? " is-on" : ""}`}
    onClick={() => onChange(!checked)}
    disabled={disabled}
  />
);

const ProfileField = ({ icon: Icon, label, value }) => (
  <label className="profile-field">
    <span className="profile-field-label">
      <Icon className="profile-field-icon" aria-hidden />
      {label}
    </span>
    <input type="text" value={value} disabled readOnly />
  </label>
);

const MyProfile = () => {
  const { projectId, phoneNoId, wabaId } = useProjectPhone();
  const [profileUser, setProfileUser] = useState(null);
  const [twoFaEnabled, setTwoFaEnabled] = useState(false);
  const [toggling2fa, setToggling2fa] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [profileImage, setProfileImage] = useState(null);
  const [profilePictureUrl, setProfilePictureUrl] = useState("");
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [waForm, setWaForm] = useState({
    about: "",
    address: "",
    description: "",
    email: "",
    websites: "",
    vertical: "",
  });

  const loadProfile = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const profilePath = projectId
        ? `/getInteractedCustomerDetail/profile?projectId=${encodeURIComponent(projectId)}`
        : "/getInteractedCustomerDetail/profile";
      const waPath = projectId
        ? `/getInteractedCustomerDetail/whatsapp_business_profile?projectId=${encodeURIComponent(projectId)}`
        : "/getInteractedCustomerDetail/whatsapp_business_profile";

      const [profileRes, waRes] = await Promise.all([
        apihubGet(profilePath),
        apihubGet(waPath).catch(() => null),
      ]);
      const user = profileRes?.user || profileRes;
      setProfileUser(user);
      const matched = user?.matchedUser || user;
      if (matched?.otpverification) setTwoFaEnabled(true);

      const wa = waRes?.data?.[0] || {};
      setProfilePictureUrl(wa.profile_picture_url || "");
      setProfileImagePreview(null);
      setWaForm({
        about: wa.about || "",
        address: wa.address || "",
        description: wa.description || "",
        email: wa.email || matched?.email || "",
        websites: Array.isArray(wa.websites)
          ? wa.websites.join(", ")
          : wa.websites || "",
        vertical: wa.vertical || "",
      });
    } catch (err) {
      setError(err.message || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    loadProfile();
  }, [loadProfile]);

  useEffect(() => {
    return () => {
      if (profileImagePreview?.startsWith("blob:")) {
        URL.revokeObjectURL(profileImagePreview);
      }
    };
  }, [profileImagePreview]);

  const matched = profileUser?.matchedUser || profileUser || {};

  const getFieldValue = (field) => {
    const raw = field.altKey
      ? matched[field.key] || matched[field.altKey]
      : matched[field.key];
    return displayValue(raw);
  };

  const toggle2fa = async () => {
    const email = matched.email;
    if (!email) {
      setError("Email not found for 2FA toggle");
      return;
    }
    setToggling2fa(true);
    setError("");
    try {
      await apihubPost("/getInteractedCustomerDetail/toggletwofactorauthentication", {
        email,
        enabled: !twoFaEnabled,
      });
      setTwoFaEnabled(!twoFaEnabled);
      setMessage(`2FA ${twoFaEnabled ? "disabled" : "enabled"} successfully`);
    } catch (err) {
      setError(err.message || "Failed to toggle 2FA");
    } finally {
      setToggling2fa(false);
    }
  };

  const onWaChange = (e) => {
    const { name, value } = e.target;
    setWaForm((prev) => ({ ...prev, [name]: value }));
  };

  const onImageChange = (e) => {
    const file = e.target.files?.[0];
    setProfileImage(file || null);
    setProfileImagePreview((prev) => {
      if (prev?.startsWith("blob:")) URL.revokeObjectURL(prev);
      return file ? URL.createObjectURL(file) : null;
    });
  };

  const displayedProfileImage = profileImagePreview || profilePictureUrl;

  const saveWhatsAppProfile = async (e) => {
    e.preventDefault();
    const sites = (waForm.websites || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (sites.length > 2) {
      setError("You can only enter up to 2 websites.");
      return;
    }

    setSaving(true);
    setError("");
    setMessage("");
    const fd = new FormData();
    fd.append("about", waForm.about || "");
    fd.append("address", waForm.address || "");
    fd.append("description", waForm.description || "");
    fd.append("email", waForm.email || "");
    fd.append("websites", waForm.websites || "");
    fd.append("vertical", waForm.vertical || "");
    fd.append("currentphoneNumberId", phoneNoId || "");
    fd.append("currentwabaId", wabaId || "");
    if (projectId) fd.append("projectId", projectId);
    if (profileImage) fd.append("profileImage", profileImage);

    try {
      await apihubUpload(
        "/getInteractedCustomerDetail/updateprofileproject",
        fd,
      );
      setMessage("WhatsApp business profile updated.");
      setProfileImage(null);
      await loadProfile();
    } catch (err) {
      setError(err.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="profile-page">
        <p className="profile-loading">Loading profile…</p>
      </div>
    );
  }

  return (
    <div className="profile-page">
      {message && <p className="profile-alert profile-alert-success">{message}</p>}
      {error && <p className="profile-alert profile-alert-error">{error}</p>}

      <div className="profile-top-grid">
        <section className="profile-card">
          <h2 className="profile-card-title">
            <FaUser aria-hidden />
            User Profile
          </h2>
          <div className="profile-fields">
            {PROFILE_FIELDS.map((field) => (
              <ProfileField
                key={field.key}
                icon={field.icon}
                label={field.label}
                value={getFieldValue(field)}
              />
            ))}
          </div>
        </section>

        <section className="profile-card profile-tfa-card">
          <FaShieldAlt className="profile-tfa-icon" aria-hidden />
          <h2 className="profile-tfa-title">Two Factor Authentication</h2>
          <p className="profile-tfa-desc">
            Add an extra layer of security to your account by enabling two-factor
            authentication. This will require a verification code when signing in
            from new devices.
          </p>
          <Toggle
            checked={twoFaEnabled}
            onChange={() => toggle2fa()}
            disabled={toggling2fa}
            label="Two factor authentication"
          />
        </section>
      </div>

      <section className="profile-card profile-wa-card">
        <h2 className="profile-card-title">
          <FaUser aria-hidden />
          Update Whatsapp Profile
        </h2>
        <form className="profile-wa-form" onSubmit={saveWhatsAppProfile}>
          <div className="profile-wa-grid">
            <label className="profile-wa-field">
              <input
                type="text"
                name="about"
                value={waForm.about}
                onChange={onWaChange}
                placeholder="About your business"
              />
            </label>
            <label className="profile-wa-field">
              <input
                type="text"
                name="address"
                value={waForm.address}
                onChange={onWaChange}
                placeholder="Business Address"
              />
            </label>
            <label className="profile-wa-field">
              <input
                type="email"
                name="email"
                value={waForm.email}
                onChange={onWaChange}
                placeholder="Business Email"
              />
            </label>
            <label className="profile-wa-field">
              <textarea
                name="description"
                rows={3}
                value={waForm.description}
                onChange={onWaChange}
                placeholder="Business Description"
              />
            </label>
            <label className="profile-wa-field">
              <input
                name="websites"
                value={waForm.websites}
                onChange={onWaChange}
                placeholder="Websites (comma-separated, max 2)"
              />
            </label>
            <label className="profile-wa-field">
              <select name="vertical" value={waForm.vertical} onChange={onWaChange}>
                <option value="">Select vertical</option>
                {VERTICALS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </label>
            <label className="profile-wa-field profile-wa-field-full">
              <span className="profile-wa-file-label">Profile image</span>
              {displayedProfileImage ? (
                <div className="profile-wa-image-preview">
                  <img
                    src={displayedProfileImage}
                    alt="WhatsApp business profile"
                  />
                </div>
              ) : (
                <p className="profile-wa-image-empty">No profile image set</p>
              )}
              <input type="file" accept="image/*" onChange={onImageChange} />
            </label>
          </div>
          <div className="profile-wa-actions">
            <button type="submit" className="profile-save-btn" disabled={saving}>
              {saving ? "Saving…" : "Update WhatsApp Profile"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
};

export default MyProfile;
